<template>
  <div id="whale-backtester-market-timing-tab">
    <helpers-section-header class="mt-0 mb-6"> Market Timing Strategies </helpers-section-header>
    <p>
      Use this section to look for recession indicators and adjust your portfolio's risk exposure
      when a trigger is met.
    </p>

    <p>
      You can configure a moving average to trigger your selling strategy, or configure macro
      recession indicators be met before any risk adjustments occur. You can use both triggers
      together.
    </p>
    <v-container fluid pa-0>
      <v-checkbox v-model="criteria.enabled" label="Enable Market Timing" hide-details />
      <helpers-section-sub-header
        >% of Long Portfolio to move to cash when sell signal is indicated (0 -
        100)</helpers-section-sub-header
      >
      <v-row>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="criteria.percent_of_portfolio_to_cash"
            label="% of Portfolio"
            hint="% of portfolio moved to cash when in a recession"
            persistent-hint
          />
        </v-col>
      </v-row>
      <helpers-section-sub-header
        >Further prevent any new buys while sell signal is active</helpers-section-sub-header
      >
      <v-row>
        <v-col cols="12" md="6">
          <v-checkbox
            v-model="criteria.freeze_new_buys"
            label="Prevent New Buys During Recession"
            color="primary"
            hide-details
            class="mt-0"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <helpers-section-sub-header class="mb-2">
            Which indicators trigger sell signal rules?
          </helpers-section-sub-header>
          <v-radio-group v-model="criteria.and_or_indicators" row hide-details>
            <v-radio
              label="All checked indicators must match before triggering"
              value="and"
              color="primary"
              hint=""
              persistent-hint
            />
            <v-radio
              label="Any checked indicator that matches will trigger"
              value="or"
              color="primary"
              hint=""
              persistent-hint
            />
          </v-radio-group>
        </v-col>
        <v-col cols="12">
          <helpers-section-sub-header class="mb-2">
            Check all indicators you want to use below
          </helpers-section-sub-header>
        </v-col>
        <v-table class="my-8">
          <tbody>
            <tr>
              <td class="py-4">
                <v-checkbox
                  v-model="criteria.moving_average.enabled"
                  label="Index Price Drops Below Moving Average"
                  color="primary"
                  hide-details
                  class="mt-0"
                />
              </td>
              <td colspan="3">
                <v-text-field
                  v-model="criteria.moving_average.months"
                  style="max-width: 300px"
                  type="number"
                  label="moving average months"
                />
                <v-select
                  v-model="criteria.moving_average.index"
                  style="max-width: 300px"
                  :items="indices"
                  item-title="text"
                  hide-details
                  label="Choose Index for Moving Average"
                />
              </td>
            </tr>
            <tr class="mt-4">
              <td class="py-4">
                <v-checkbox
                  v-model="criteria.index_performance.enabled"
                  label="Price Appreciation Above Moving Avg (overbought signal)"
                  color="primary"
                  hide-details
                  class="mt-0"
                />
              </td>

              <td colspan="3">
                <p style="max-width: 400px">
                  Use this option to trigger selling when, for example, the s&amp;p moves X% above
                  it's 200 day moving average or when the S&amp;P's 50 day moving average moves XX%
                  above it's 200 day ma.
                </p>
                <v-row density="compact">
                  <v-col cols="12" sm="6">
                    <v-select
                      v-model="criteria.index_performance.index"
                      style="max-width: 300px"
                      :items="indices"
                      item-title="text"
                      hide-details
                      label="Choose Index for Moving Average"
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="criteria.index_performance.days"
                      style="max-width: 300px"
                      type="number"
                      label="# of days for moving average"
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-select
                      v-model="criteria.index_performance.compare_to"
                      style="max-width: 300px"
                      :items="['price', 'moving average']"
                      hide-details
                      label="Compare to"
                    />
                  </v-col>
                  <v-col
                    v-if="criteria.index_performance.compare_to == 'moving average'"
                    cols="12"
                    sm="6"
                  >
                    <v-text-field
                      v-model="criteria.index_performance.compare_to_ma"
                      style="max-width: 300px"
                      type="number"
                      label="Days"
                      hint="Moving average days"
                      persistent-hint
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="criteria.index_performance.percent_increase"
                      style="max-width: 300px"
                      type="number"
                      label="Percent Increase Over first moving average"
                    />
                  </v-col>
                </v-row>
              </td>
            </tr>
            <tr>
              <th>Macro Indicator</th>
              <th>Above/Below</th>
              <th>Threshold</th>
              <th />
            </tr>
            <tr v-for="m in criteria.recession_indicators" :key="m.macro_data_type_id">
              <td class="py-4">
                <v-checkbox
                  v-model="m.enabled"
                  :label="m.description"
                  color="primary"
                  hide-details
                  class="mt-0"
                />
              </td>
              <td class="py-4">
                <v-select v-model="m.above_below" :items="above_below" class="shrink" />
              </td>
              <td class="py-4">
                <v-text-field v-model="m.threshold" />
              </td>
              <td>
                <v-checkbox
                  v-if="m.macro_data_type_id == 4"
                  v-model="m.require_other_enabled"
                  label="Combine with Real Personal Income Indicator"
                  color="primary"
                  hide-details
                  class="mt-0"
                />
              </td>
            </tr>
          </tbody>
        </v-table>
      </v-row>

      <helpers-section-sub-header
        >Rule(s) that determine when to go back to 100% investible</helpers-section-sub-header
      >
      <v-row>
        <v-col cols="12">
          <p>When these conditions are met you will resume normal trading</p>
        </v-col>
        <v-col cols="12">
          <v-radio-group v-model="criteria.resume_and_or_indicators" row hide-details>
            <v-radio
              label="All checked indicators must match before triggering"
              value="and"
              color="primary"
              hint=""
              persistent-hint
            />
            <v-radio
              label="Any checked indicator that matches will trigger"
              value="or"
              color="primary"
              hint=""
              persistent-hint
            />
          </v-radio-group>
        </v-col>
        <v-table class="my-8">
          <thead>
            <tr>
              <th>Rule</th>
              <th>Above/Below</th>
              <th>Threshold</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="m in criteria.resume_indicators" :key="m.id">
              <td class="py-4">
                <v-checkbox
                  v-model="m.enabled"
                  :disabled="IsDisabledResumeIndicator(m)"
                  :label="m.description"
                  color="primary"
                  hide-details
                  class="mt-0"
                />
                <p v-if="m.id == 1">
                  Available when "Index Price Drops Below Moving Average" sell trigger is used.
                </p>
                <p v-if="m.id == 4">
                  Available when "Price Appreciation Above Moving Avg" sell trigger is used and you
                  are comparing two moving averages.
                </p>
                <p
                  v-if="
                    m.id == 4 &&
                    criteria.index_performance.enabled &&
                    criteria.index_performance.compare_to == 'moving average'
                  "
                >
                  Will resume regular trading when index
                  {{ criteria.index_performance.compare_to_ma }} day moving average moves below the
                  index {{ criteria.index_performance.days }} day moving average
                </p>
              </td>
              <td class="py-4">
                <v-select
                  v-show="m.id == 2"
                  v-model="m.above_below"
                  :items="above_below"
                  class="shrink"
                />
                <v-select
                  v-show="m.id == 3"
                  v-model="m.index"
                  style="max-width: 300px"
                  :items="indices"
                  item-title="text"
                  hide-details
                  label="Choose Index"
                />
              </td>
              <td class="py-4">
                <v-text-field v-show="m.id == 2" v-model="m.threshold" />
                <v-text-field
                  v-show="m.id == 3"
                  v-model="m.threshold"
                  hide-details
                  label="% decline in index after sell trigger"
                />
              </td>
            </tr>
          </tbody>
        </v-table>
      </v-row>
    </v-container>
  </div>
</template>

<script>
// Modules
import { mapStoreState, mapFields } from '@/store/helpers'
import { useUserStore, useAppStore, useWwStore } from '@/store'
import AccessMixin from '@/mixins/access'

export default {
  mixins: [AccessMixin],
  data() {
    return {
      indices: [
        { value: 169819, text: 'S&P 500  Total Return Index' },
        { value: 169823, text: 'Russell 2000 Total Return Index' }
      ],
      above_below: ['above', 'below']
    }
  },
  computed: {
    ...mapStoreState('ww', ['sectors']),
    ...mapFields({
      appcriteria: 'app.whaleTraderCriteria',
      criteria: 'app.whaleTraderCriteria.market_timing',
      signalView: 'app.signalView'
    })
  },
  methods: {
    IsDisabledResumeIndicator(m) {
      var disabled = false
      if (m.id == 1 && !this.criteria.moving_average.enabled) {
        disabled = true
      } else if (
        m.id == 4 &&
        (!this.criteria.index_performance.enabled ||
          this.criteria.index_performance.compare_to != 'moving average')
      ) {
        disabled = true
      }
      return disabled
    }
  }
}
</script>
